import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTachometerAlt,
  faUsers,
  faFileAlt,
  faChartBar,
  faLungs,
  faBrain,
  faUser,
  faHistory,
  faSignOutAlt,
  faUserPlus,
  faCog,
  faEnvelope,
} from '@fortawesome/free-solid-svg-icons';

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userRef = doc(db, 'users', currentUser.uid);
        try {
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            
            console.log('Raw user data:', userData);
            console.log('Status:', userData.status, 'Type:', typeof userData.status);

            // Check status - case insensitive comparison
            const status = userData.status ? userData.status.toString().toLowerCase() : null;
            console.log('Status check in Dashboard. Raw status:', userData.status, 'Normalized status:', status);
            if (status !== 'enable' && status !== 'enabled') {
              console.log('Status check failed. Current status:', status);
              await signOut(auth);
              navigate('/login', { 
                replace: true,
                state: { error: 'Your account has been disabled. Please contact administrator for assistance.' }
              });
              return;
            }

            // Check role validity
            const allowedRoles = ['admin', 'user'];
            if (!userData.role || !allowedRoles.includes(userData.role)) {
              console.log('Role check failed:', userData.role);
              await signOut(auth);
              navigate('/', { 
                replace: true,
                state: { error: 'Access denied. Insufficient privileges.' }
              });
              return;
            }

            console.log('User authenticated successfully:', userData);
            setUser(userData);
          } else {
            console.log('User document does not exist. Creating new document...');
            let initialCredits = 30;
            try {
              const planRef = doc(db, 'planSettings', 'basic');
              const planSnap = await getDoc(planRef);
              if (planSnap.exists()) {
                initialCredits = planSnap.data().credits || 30;
              } else {
                console.warn('Basic plan not found. Using default credits: 30');
                await setDoc(planRef, {
                  id: 'basic',
                  name: 'Basic',
                  credits: 30,
                  price: 0,
                });
              }
            } catch (err) {
              console.error('Error fetching plan credits:', err);
              setError('Failed to fetch plan credits. Using default value.');
            }

            const newUserData = {
              displayName: currentUser.displayName || currentUser.email.split('@')[0],
              username: currentUser.email.split('@')[0],
              email: currentUser.email,
              status: 'enable',
              plan: 'basic',
              credits: initialCredits,
              role: 'user',
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };
            await setDoc(userRef, newUserData);
            setUser(newUserData);
            console.log('User document created with status:', newUserData.status);
          }
        } catch (err) {
          console.error('Error accessing user document:', err);
          setError('Failed to load user data: ' + err.message);
          await signOut(auth);
          navigate('/login', { 
            replace: true,
            state: { error: 'Failed to load user data. Please try again.' }
          });
        }
      } else {
        console.log('No current user detected');
        setUser(null);
        navigate('/login', { replace: true });
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      console.log('User signed out successfully');
      navigate('/', { replace: true }); // Redirect to homepage/landing page
      setUser(null);
    } catch (err) {
      console.error('Logout error:', err);
      setError('Failed to log out: ' + err.message);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen((prevState) => !prevState);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 p-6 rounded-lg shadow-md text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 p-6 rounded-lg shadow-md text-center">
          <p className="text-red-600 mb-4">User not found. Please log in again.</p>
          <Link to="/login" className="text-purple-600 underline">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  // Rest of your JSX (sidebar and main content) remains unchanged
  return (
    <div className="flex min-h-screen font-['Noto Sans'] flex-col">
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 bg-gray-800 text-white w-64 transform ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0 transition-transform duration-300 ease-in-out z-30 flex flex-col h-full`}
      >
        {/* Logo Section */}
        <div className="p-4 flex flex-col items-center border-b border-gray-700">
          <img src="/assets/medpredix-logo-white.svg" alt="Medpredix Logo" className="h-12 mb-2" />
        </div>
        
        {/* Navigation Section - Scrollable */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <nav className="py-2">
            <Link to="/dashboard/overview" className="flex items-center px-4 py-3 hover:bg-gray-700 transition-colors duration-200" onClick={() => setIsSidebarOpen(false)}>
              <FontAwesomeIcon icon={faTachometerAlt} className="mr-3 text-blue-400" />
              <span>Overview</span>
            </Link>
            
            {/* Diagnosis Section */}
            <div className="px-4 pt-6 pb-2">
              <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold">Diagnosis</p>
            </div>
            
            <Link to="/dashboard/pneumonia" className="flex items-center px-4 py-3 hover:bg-gray-700 transition-colors duration-200" onClick={() => setIsSidebarOpen(false)}>
              <FontAwesomeIcon icon={faLungs} className="mr-3 text-green-400" />
              <span>Pneumonia</span>
            </Link>
            <Link to="/dashboard/brain-tumor" className="flex items-center px-4 py-3 hover:bg-gray-700 transition-colors duration-200" onClick={() => setIsSidebarOpen(false)}>
              <FontAwesomeIcon icon={faBrain} className="mr-3 text-pink-400" />
              <span>Brain Tumor</span>
            </Link>
            
            {/* Analysis Section */}
            <div className="px-4 pt-6 pb-2">
              <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold">Analysis</p>
            </div>
            
            <Link to="/dashboard/history" className="flex items-center px-4 py-3 hover:bg-gray-700 transition-colors duration-200" onClick={() => setIsSidebarOpen(false)}>
              <FontAwesomeIcon icon={faHistory} className="mr-3 text-yellow-400" />
              <span>History</span>
            </Link>
            <Link to="/dashboard/reports" className="flex items-center px-4 py-3 hover:bg-gray-700 transition-colors duration-200" onClick={() => setIsSidebarOpen(false)}>
              <FontAwesomeIcon icon={faChartBar} className="mr-3 text-purple-400" />
              <span>Reports</span>
            </Link>
            <Link to="/dashboard/plans" className="flex items-center px-4 py-3 hover:bg-gray-700 transition-colors duration-200" onClick={() => setIsSidebarOpen(false)}>
              <FontAwesomeIcon icon={faFileAlt} className="mr-3 text-indigo-400" />
              <span>Plans</span>
            </Link>
            
            {/* Admin Section - Only shown to admins */}
            {user.role === 'admin' && (
              <>
                <div className="px-4 pt-6 pb-2">
                  <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold">Admin</p>
                </div>
                
                <Link to="/dashboard/customers" className="flex items-center px-4 py-3 hover:bg-gray-700 transition-colors duration-200" onClick={() => setIsSidebarOpen(false)}>
                  <FontAwesomeIcon icon={faUsers} className="mr-3 text-blue-300" />
                  <span>Customers</span>
                </Link>
                <Link to="/dashboard/join-requests" className="flex items-center px-4 py-3 hover:bg-gray-700 transition-colors duration-200" onClick={() => setIsSidebarOpen(false)}>
                  <FontAwesomeIcon icon={faUserPlus} className="mr-3 text-green-300" />
                  <span>Join Requests</span>
                </Link>
                <Link to="/dashboard/messages" className="flex items-center px-4 py-3 hover:bg-gray-700 transition-colors duration-200" onClick={() => setIsSidebarOpen(false)}>
                  <FontAwesomeIcon icon={faEnvelope} className="mr-3 text-yellow-300" />
                  <span>Messages</span>
                </Link>
                <Link to="/dashboard/settings" className="flex items-center px-4 py-3 hover:bg-gray-700 transition-colors duration-200" onClick={() => setIsSidebarOpen(false)}>
                  <FontAwesomeIcon icon={faCog} className="mr-3 text-gray-300" />
                  <span>Settings</span>
                </Link>
              </>
            )}
          </nav>
        </div>

        {/* User Account Section - Modern Style */}
        <div className="p-4 border-t border-gray-700 bg-gray-900">
          <div className="flex items-center justify-between">
            <Link 
              to="/dashboard/profile" 
              className="flex items-center group"
              onClick={() => setIsSidebarOpen(false)}
            >
              <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center">
                <FontAwesomeIcon icon={faUser} className="text-white group-hover:scale-110 transition-transform duration-200" />
              </div>
              <span className="ml-3 text-purple-300 font-semibold group-hover:text-white transition-colors duration-200">Profile</span>
            </Link>
            
            <button 
              onClick={handleLogout} 
              className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center hover:bg-red-700 transition-colors duration-200 group"
              title="Logout"
            >
              <FontAwesomeIcon icon={faSignOutAlt} className="text-white group-hover:rotate-180 transition-transform duration-500" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 md:ml-64 flex flex-col min-h-screen">
        <div className="p-4 flex-1">
          <button className="md:hidden p-2 text-gray-600" onClick={toggleSidebar}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" />
            </svg>
          </button>
          <Outlet />
        </div>

        <footer className="w-full bg-gray-800 text-white p-4 text-center">
          <p className="text-sm">Powered by Hilyoon SRL © {new Date().getFullYear()}</p>
        </footer>
      </div>

      {isSidebarOpen && (
        <div className="fixed inset-0 bg-black opacity-50 md:hidden z-20" onClick={() => setIsSidebarOpen(false)} />
      )}
    </div>
  );
};

export default Dashboard;